import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/shared/PageHeader';
import { dbClient } from '@/services/db-client';
import { cn } from '@/lib/utils';
import { Save, Loader2, RotateCcw, Gift } from 'lucide-react';
import { ChestContextEditor } from './ChestContextEditor';
import { MerchTab } from './MerchTab';
import { ChestPrizeTrackingTab } from './ChestPrizeTrackingTab';
import {
    CONTEXT_TABS,
    normalizeContextConfig,
    type ChestConfigResponse,
    type ChestContextConfig,
    type ChestContextKey,
    type ChestRewardConfig,
    type EquipmentCatalogItem,
    type MerchControlData,
} from './chestConfigTypes';
import { sumEnabledWeights } from './chestConfigUtils';

const TAB_STORAGE_KEY = 'admin-cofres-tab';

function normalizeMerch(raw: unknown): MerchControlData {
    const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const weightsRaw = (obj.weights && typeof obj.weights === 'object' ? obj.weights : {}) as Record<
        string,
        number
    >;
    const quotasArr = Array.isArray(obj.quotas)
        ? (obj.quotas as MerchControlData['quotas'])
        : [];
    return {
        weights: weightsRaw,
        quotas: quotasArr,
        period_week: String(obj.period_week ?? ''),
        super_treasure_weekly: obj.super_treasure_weekly as MerchControlData['super_treasure_weekly'],
    };
}

function unwrapRpcPayload(raw: unknown): Record<string, unknown> {
    if (!raw) return {};
    if (Array.isArray(raw)) {
        const first = raw[0];
        return first && typeof first === 'object' ? (first as Record<string, unknown>) : {};
    }
    return typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
}

function normalizeEquipmentCatalog(raw: unknown): EquipmentCatalogItem[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .filter((row): row is Record<string, unknown> => row && typeof row === 'object')
        .map((row) => ({
            id: String(row.id ?? ''),
            name: String(row.name ?? row.id ?? ''),
            tier: String(row.tier ?? ''),
            category: String(row.category ?? ''),
        }))
        .filter((row) => row.id.length > 0);
}

function normalizeResponse(raw: unknown): ChestConfigResponse {
    const obj = unwrapRpcPayload(raw);
    const config = (obj.config && typeof obj.config === 'object' ? obj.config : {}) as ChestRewardConfig;
    return {
        config,
        merch: normalizeMerch(obj.merch),
        equipmentCatalog: normalizeEquipmentCatalog(obj.equipment_catalog ?? obj.equipmentCatalog),
    };
}

export function CofresPage() {
    const queryClient = useQueryClient();
    type AdminChestTab = ChestContextKey | 'merch' | 'tracking';
    const [activeTab, setActiveTab] = useState<AdminChestTab>(() => {
        const saved = sessionStorage.getItem(TAB_STORAGE_KEY);
        if (saved === 'merch' || saved === 'tracking') return saved;
        if (CONTEXT_TABS.some((tab) => tab.key === saved)) return saved as AdminChestTab;
        return 'capture';
    });
    const [contexts, setContexts] = useState<Record<string, ChestContextConfig>>({});
    const [equipmentCatalog, setEquipmentCatalog] = useState<EquipmentCatalogItem[]>([]);
    const [merch, setMerch] = useState<MerchControlData | null>(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [saveOk, setSaveOk] = useState(false);

    const { data: saved, isLoading } = useQuery({
        queryKey: ['admin', 'chest-config'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<ChestConfigResponse>('admin_get_chest_config');
            if (error) throw error;
            return normalizeResponse(data);
        },
    });

    useEffect(() => {
        if (!saved) return;
        const next: Record<string, ChestContextConfig> = {};
        for (const tab of CONTEXT_TABS) {
            next[tab.key] = normalizeContextConfig(
                tab.key,
                saved.config?.contexts?.[tab.key],
                saved.equipmentCatalog
            );
        }
        setContexts(next);
        setEquipmentCatalog(saved.equipmentCatalog);
        setMerch(saved.merch);
        setHasChanges(false);
        setSaveOk(false);
    }, [saved]);

    const saveMutation = useMutation({
        mutationFn: async (ctxKey: ChestContextKey) => {
            const payload = contexts[ctxKey];
            if (!payload) throw new Error('Contexto no cargado');
            const { error } = await dbClient.rpc('admin_upsert_chest_config', {
                p_context: ctxKey,
                p_config: payload,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'chest-config'] });
            setHasChanges(false);
            setSaveOk(true);
        },
    });

    const activeMeta = CONTEXT_TABS.find((t) => t.key === activeTab);
    const activeContext = activeTab !== 'merch' ? contexts[activeTab] : null;

    const isValid = useMemo(() => {
        if (activeTab === 'merch' || activeTab === 'tracking' || !activeContext || !activeMeta) return true;
        if (!activeMeta.percentMode) return true;
        return Math.abs(sumEnabledWeights(activeContext) - 100) < 0.5;
    }, [activeTab, activeContext, activeMeta]);

    const handleTabChange = (tab: AdminChestTab) => {
        setActiveTab(tab);
        sessionStorage.setItem(TAB_STORAGE_KEY, tab);
        setSaveOk(false);
    };

    const handleContextChange = (key: ChestContextKey, next: ChestContextConfig) => {
        setContexts((prev) => ({ ...prev, [key]: next }));
        setHasChanges(true);
        setSaveOk(false);
    };

    const resetTab = () => {
        if (!saved || activeTab === 'merch' || activeTab === 'tracking') return;
        handleContextChange(activeTab, normalizeContextConfig(activeTab, saved.config?.contexts?.[activeTab], saved.equipmentCatalog));
        setHasChanges(false);
    };

    if (isLoading || !merch) {
        return (
            <div className="py-24 flex items-center justify-center">
                <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-5xl">
            <PageHeader
                title="Cofres"
                description="Probabilidades y activación de recompensas por contexto: capturas, batallas, transmutar, e-Pesca y quiz."
            >
                {activeTab !== 'merch' && activeTab !== 'tracking' && (
                    <>
                        <button
                            onClick={resetTab}
                            disabled={!hasChanges}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground disabled:opacity-40"
                        >
                            <RotateCcw size={14} />
                            Descartar
                        </button>
                        <button
                            onClick={() => saveMutation.mutate(activeTab as ChestContextKey)}
                            disabled={!hasChanges || !isValid || saveMutation.isPending}
                            className={cn(
                                'inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium',
                                hasChanges && isValid
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted text-muted-foreground cursor-not-allowed'
                            )}
                        >
                            {saveMutation.isPending ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Save size={14} />
                            )}
                            Guardar
                        </button>
                    </>
                )}
            </PageHeader>

            <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-muted/30 border border-border/50">
                {CONTEXT_TABS.map((tab) => (
                    <button
                        key={tab.key}
                        onClick={() => handleTabChange(tab.key)}
                        className={cn(
                            'px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors',
                            activeTab === tab.key
                                ? 'bg-background text-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        {tab.label}
                    </button>
                ))}
                <button
                    onClick={() => handleTabChange('merch')}
                    className={cn(
                        'px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors inline-flex items-center gap-1',
                        activeTab === 'merch'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                    )}
                >
                    <Gift size={14} />
                    Merch
                </button>
                <button
                    onClick={() => handleTabChange('tracking')}
                    className={cn(
                        'px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors',
                        activeTab === 'tracking'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                    )}
                >
                    Seguimiento
                </button>
            </div>

            {activeTab === 'merch' ? (
                <MerchTab saved={merch} onSaved={() => setSaveOk(true)} />
            ) : activeTab === 'tracking' ? (
                <ChestPrizeTrackingTab />
            ) : activeMeta && activeContext ? (
                <>
                    <p className="text-sm text-muted-foreground">{activeMeta.description}</p>
                    <ChestContextEditor
                        contextKey={activeTab as ChestContextKey}
                        config={activeContext}
                        equipmentCatalog={equipmentCatalog}
                        onChange={(next) => handleContextChange(activeTab as ChestContextKey, next)}
                        percentMode={activeMeta.percentMode}
                    />
                </>
            ) : null}

            {saveOk && (
                <div className="px-4 py-3 rounded-xl border bg-emerald-500/5 border-emerald-500/20 text-sm text-emerald-400">
                    Configuración guardada. Se aplicará en la próxima tirada.
                </div>
            )}
            {saveMutation.isError && (
                <div className="px-4 py-3 rounded-xl border bg-red-500/5 border-red-500/20 text-sm text-red-400">
                    Error al guardar: {(saveMutation.error as Error)?.message}
                </div>
            )}
        </div>
    );
}
